import { useCallback, useState } from "react";
import { Avatar, Box, Button, Skeleton, Typography } from "@mui/material";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import {
  convertESTtoUserLocalDateAndTime,
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { useAppSelector } from "redux/hooks";
import { selectUserName } from "redux/authSlice";
import { doAddComment } from "screens/Tickets/ViewTicket/ViewTicketService";
import CommentEditor, { parseHTML } from "./CommentEditor";
import TicketAttachments from "./Attachments";
import viewTicketTokens from "../ViewTicket.tokens";
import {
  avatarSx,
  cardSx,
  cardTitleSx,
  commentAuthorRowSx,
  commentAuthorSx,
  commentBodySx,
  commentPlaceholderSx,
  commentRowSx,
  commentTimestampSx,
  inlineEditorSx,
  noCommentsSx,
  replyLinkSx,
  replyThreadSx,
  threadSx,
} from "../ViewTicket.styles";
import EmptyState from "global/components/EmptyState/EmptyState";

const commentTokens = viewTicketTokens.comments;

interface CustomProps {
  ticketDetails: any;
  isLoading: boolean;
  ticketId: string;
  fetchTicketById: Function;
}

const getInitials = (name?: string) => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const formatTimestamp = (timestamp?: string) =>
  timestamp
    ? convertESTtoUserLocalDateAndTime(
        timestamp,
        "MMMM D, YYYY [at] h:mm A",
        "MM/DD/YYYY HH:mm:ss",
      )
    : "";

/**
 * Comments card: the signed-in user's composer above the thread, with each
 * comment's replies nested beneath it. The composer stays collapsed to a single
 * "Add a comment" field until it is clicked, as the design shows.
 */
const Comments = (props: CustomProps) => {
  const userName = useAppSelector(selectUserName);
  const [isSaving, setIsSaving] = useState(false);
  const [files, setFiles] = useState<string[]>([]);
  const [addComment, setAddComment] = useState({
    content: "",
    showEditor: false,
  });
  const [replyComment, setReplyComment] = useState({
    showEditor: false,
    content: "",
    commentId: 0,
  });

  const comments: any[] = props.ticketDetails?.comments ?? [];

  const handleOnContentChange = useCallback((content: string) => {
    setAddComment((prevState) => ({ ...prevState, content }));
  }, []);

  const handleReplyContentChange = useCallback((content: string) => {
    setReplyComment((prevState) => ({ ...prevState, content }));
  }, []);

  const closeAddComment = () => {
    setAddComment({ content: "", showEditor: false });
    setFiles([]);
  };

  const closeReply = () => {
    setReplyComment({ showEditor: false, content: "", commentId: 0 });
    setFiles([]);
  };

  /** Quill leaves an empty paragraph behind, which is not a comment. */
  const isEmpty = (content: string) =>
    content.replace(/<[^>]*>/g, "").trim() === "";

  const handleAddComment = async () => {
    if (isEmpty(addComment.content)) {
      return openErrorNotification("Comment text is required");
    }
    try {
      setIsSaving(true);
      await doAddComment({
        text: addComment.content,
        type: "External",
        referenceId: props.ticketId,
        parentId: 0,
        docUrls: files,
      });
      await props.fetchTicketById();
      closeAddComment();
      openSuccessNotification("Comment added successfully");
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReplyComment = async (comment: any) => {
    if (isEmpty(replyComment.content)) {
      return openErrorNotification("Comment text is required");
    }
    try {
      setIsSaving(true);
      await doAddComment({
        text: replyComment.content,
        type: comment.type,
        referenceId: props.ticketId,
        parentId: comment.id,
        docUrls: files,
      });
      await props.fetchTicketById();
      closeReply();
      openSuccessNotification("Comment added successfully");
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsSaving(false);
    }
  };

  const getComment = (comment: any) => {
    const author = comment?.byName ?? comment?.byId;
    const replies: any[] = comment?.reply ?? [];
    const isReplying =
      replyComment.showEditor && replyComment.commentId === comment.id;

    return (
      <Box key={comment.id}>
        <Box sx={commentRowSx}>
          {/* Initials duplicate the author name printed right next to them; a
              screen reader would spell them out for no gain (WCAG 1.3.1). */}
          <Avatar aria-hidden="true" sx={avatarSx(false)}>
            {getInitials(author)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={commentAuthorRowSx}>
              <Typography component="span" sx={commentAuthorSx}>
                {author}
              </Typography>
              <Typography component="span" sx={commentTimestampSx}>
                {formatTimestamp(comment?.timestamp)}
              </Typography>
            </Box>

            <Typography component="div" sx={commentBodySx}>
              {parseHTML(comment.text ?? "")}
            </Typography>

            <TicketAttachments files={comment?.docUrls} variant="comment" />

            <Box>
              <Button
                disableRipple
                // Every comment repeats "Reply"; naming the target tells the
                // buttons apart out of context (WCAG 2.4.6 / 4.1.2).
                aria-label={`Reply to ${author}`}
                onClick={() =>
                  setReplyComment({
                    showEditor: true,
                    content: "",
                    commentId: comment.id,
                  })
                }
                sx={replyLinkSx}
              >
                Reply
              </Button>
            </Box>

            {isReplying && (
              <Box sx={inlineEditorSx}>
                <CommentEditor
                  content={replyComment.content}
                  onChange={handleReplyContentChange}
                  handleCancel={closeReply}
                  handleAddComment={() => handleReplyComment(comment)}
                  ticketId={props.ticketId}
                  files={files}
                  setFiles={setFiles}
                  isSaving={isSaving}
                />
              </Box>
            )}
          </Box>
        </Box>

        {replies.length > 0 && (
          <Box sx={replyThreadSx}>{replies.map(getComment)}</Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={cardSx}>
      <Typography component="h2" sx={cardTitleSx}>
        Comments
      </Typography>

      <Box sx={commentRowSx}>
        <Avatar sx={avatarSx(true)}>{getInitials(userName)}</Avatar>
        {addComment.showEditor ? (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CommentEditor
              content={addComment.content}
              onChange={handleOnContentChange}
              handleCancel={closeAddComment}
              handleAddComment={handleAddComment}
              ticketId={props.ticketId}
              files={files}
              setFiles={setFiles}
              isSaving={isSaving}
            />
          </Box>
        ) : (
          <Button
            disableRipple
            onClick={() =>
              setAddComment((prevState) => ({ ...prevState, showEditor: true }))
            }
            sx={commentPlaceholderSx}
          >
            Add a comment
          </Button>
        )}
      </Box>

      {props.isLoading && comments.length === 0 ? (
        <Box sx={threadSx}>
          {[0, 1].map((index) => (
            <Box key={index} sx={commentRowSx}>
              <Skeleton
                animation="wave"
                variant="circular"
                width={commentTokens.avatarSize}
                height={commentTokens.avatarSize}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={16}
                  width="35%"
                />
                <Skeleton animation="wave" variant="text" height={14} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : comments.length > 0 ? (
        <Box sx={threadSx}>{comments.map(getComment)}</Box>
      ) : (
        /* A thread inside a panel — the compact size, and nothing to act on:
           the reply box already sits directly beneath it. */
        <EmptyState size="compact" title="No comments yet" />
      )}

      <CustomLoader isLoading={isSaving} />
    </Box>
  );
};

export default Comments;
